import React, { Fragment, useCallback, useRef } from 'react';
import T from 'prop-types';
import { AppState, Platform } from 'react-native';
import * as StoreReview from 'expo-store-review';
import { useFocusEffect } from '@react-navigation/native';

import Bugsnag from '@dmi/shared/utils/Bugsnag';
import { PAYMENT_FLOW_TYPE_AUTOPAY } from '@dmi/shared/redux/Payments/Autopay/constants';
import { PAYMENT_FLOW_TYPES } from '@dmi/shared/redux/Payments/constants';
import { PAYMENT_FLOW_TYPE_OTP } from '@dmi/shared/redux/Payments/OneTimePayment/constants';
import { interpolate } from '@dmi/shared/utils/globalHelpers';

import { delay } from '../../../../utils/globalHelpers';
import {
  CardButton,
  ConditionalRender,
  NativeAnimation,
  P2,
  WebAnimation,
} from '../../../base_ui';
import { SecondaryHeader } from '../../../ScreenHeaders';
import { InfoSection } from '../../sharedComponents';
import { ContentWrapper } from '../../styledComponents';
import EmailErrorBanner from './EmailErrorBanner';
import { cardButtonPropsHelper } from './helpers';
import {
  AnimationWrapper,
  CancelWrapper,
  CardButtonWrapper,
  DataLabel,
  DataLine,
  DataWrapper,
  InfoSectionContainer,
  StyledBorderlessButton,
  StyledPrimaryDivider,
  SubHeader,
  SubHeaderSubText,
} from './styledComponents';

const ConfirmationSuccess = ({
  confirmationDictionary: {
    modalToOpen,
    successHeader,
    successHeaderMobile,
    successLinkLabel,
    successSubHeader,
    successText,
  },
  confirmationType,
  customerServicePhoneNumber,
  data,
  dispatchSetIsModalOpen,
  hasLateCharges,
  infoSectionData,
  isEligibleForAutopay,
  isInAppReviewSupported = false,
  isMurabahaAccount,
  modalProps,
  navigation,
  paymentFlowType,
  showEmailErrorMessage,
}) => {
  const appState = useRef(AppState.currentState);
  const isInitialMount = useRef(true);
  const { isOpen } = modalProps;

  const cardButtonProps = cardButtonPropsHelper({
    confirmationType,
    isEligibleForAutopay,
    isMurabahaAccount,
    navigation,
    paymentFlowType,
  });

  useFocusEffect(
    useCallback(() => {
      /**
       * We only want the requestReview flow to happen on first render, not every time the useEffect
       * dependencies change. We listen for any change of dependencies (namely isCancelModalOpen)
       * simply to trigger the cleanup/cancellation.
       */
      const subscription = AppState.addEventListener('change', (nextAppState) => {
        appState.current = nextAppState;
      });
      let isCancelled = false;

      const shouldInitiateReviewFlow =
        isInitialMount.current
        && paymentFlowType === PAYMENT_FLOW_TYPE_OTP
        && isInAppReviewSupported
        && !hasLateCharges;

      if (shouldInitiateReviewFlow) {
        isInitialMount.current = false;

        (async () => {
          await delay(2500);
          if (appState.current === 'active' && !isCancelled && !isOpen) {
            try {
              await StoreReview.requestReview();
            } catch (error) {
              Bugsnag.notify(error);
            }
          }
        })();
      }

      return () => {
        isCancelled = true;
        subscription.remove();
      };
    }, [
      hasLateCharges,
      isInAppReviewSupported,
      isOpen,
      paymentFlowType,
    ]),
  );

  return (
    <Fragment>
      <SecondaryHeader
        handleDone={() => navigation.navigateDeprecated('Payments')}
        title={successHeaderMobile || successHeader}
      />
      <ContentWrapper>
        <AnimationWrapper>
          <ConditionalRender
            Component={<WebAnimation name="flyingMoneyMobile" />}
            FallbackComponent={<NativeAnimation name="flyingMoneyMobile" />}
            shouldRender={Platform.OS === 'web'}
          />
        </AnimationWrapper>
        <SubHeader>{successSubHeader}</SubHeader>
        <SubHeaderSubText>{successText}</SubHeaderSubText>
        <ConditionalRender Component={EmailErrorBanner} shouldRender={!!showEmailErrorMessage} />
        <DataWrapper>
          {data.map(({
            isNotBold,
            label,
            value,
          }, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <DataLine key={i}>
              <DataLabel $isNotBold={isNotBold}>{label}</DataLabel>
              <P2>{value}</P2>
            </DataLine>
          ))}
        </DataWrapper>
        <ConditionalRender
          Component={(
            <Fragment>
              <StyledPrimaryDivider />
              <CancelWrapper>
                <StyledBorderlessButton
                  label={successLinkLabel}
                  onPress={() => dispatchSetIsModalOpen({ key: modalToOpen, value: true })}
                />
              </CancelWrapper>
            </Fragment>
          )}
          shouldRender={!!successLinkLabel}
        />
        <ConditionalRender
          Component={() => (
            <InfoSectionContainer $isAutopay={paymentFlowType === PAYMENT_FLOW_TYPE_AUTOPAY}>
              <InfoSection
                customerServicePhoneNumber={customerServicePhoneNumber}
                data={infoSectionData.map(({ text, variant, ...restProps }) => ({
                  text: interpolate(text, { customerServicePhoneNumber }),
                  variant,
                  ...restProps,
                }))}
              />
            </InfoSectionContainer>
          )}
          shouldRender={!!infoSectionData}
        />
        <ConditionalRender
          Component={() => (
            <CardButtonWrapper>
              <CardButton {...cardButtonProps} />
            </CardButtonWrapper>
          )}
          shouldRender={!!cardButtonProps}
        />
      </ContentWrapper>
    </Fragment>
  );
};

ConfirmationSuccess.propTypes = {
  confirmationDictionary: T.shape({
    modalToOpen: T.string,
    successHeader: T.string.isRequired,
    successHeaderMobile: T.string,
    successLinkLabel: T.string.isRequired,
    successSubHeader: T.string.isRequired,
    successText: T.string.isRequired,
  }).isRequired,
  confirmationType: T.string,
  customerServicePhoneNumber: T.string,
  data: T.arrayOf(T.shape({
    label: T.string.isRequired,
    value: T.string.isRequired,
  })).isRequired,
  dispatchSetIsModalOpen: T.func.isRequired,
  hasLateCharges: T.bool,
  infoSectionData: T.array,
  isEligibleForAutopay: T.bool,
  isInAppReviewSupported: T.bool,
  isMurabahaAccount: T.bool.isRequired,
  modalProps: T.shape({ isOpen: T.bool.isRequired }).isRequired,
  navigation: T.object.isRequired,
  paymentFlowType: T.oneOf(PAYMENT_FLOW_TYPES).isRequired,
  showEmailErrorMessage: T.bool,
};

export default ConfirmationSuccess;
